import React, { useState } from 'react';
import { Alert, Button, DatePicker, Form, Modal, Space } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TransportRouteDetailDto } from '../../../dto/transport';
import { transportDatePickerProps } from '../../../components/transportFormUtils';
import TransportDeliveryQuantityFields, {
  buildTransportDeliveryFormItems,
  type TransportDeliveryFormItem,
} from './TransportDeliveryQuantityFields';

export interface TransportDeliveryConfirmation {
  status: 'DELIVERED';
  actualDelivery: string;
  deliveryItems: TransportDeliveryFormItem[];
}

interface Props {
  transport: TransportRouteDetailDto;
  onCancel: () => void;
  onConfirm: (payload: TransportDeliveryConfirmation) => Promise<boolean>;
}

/** Mount only after the complete truck is loaded; never seed from an order's cargo slice. */
export default function TransportDeliveryConfirmationModal({ transport, onCancel, onConfirm }: Props) {
  const { t } = useTranslation(['transport', 'common']);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const missingIdentity = transport.cargoItems.some(item => !item.orderProductId);
  const save = async (values: { actualDelivery: Dayjs; deliveryItems: TransportDeliveryFormItem[] }) => {
    if (saving || missingIdentity) return;
    setSaving(true);
    try {
      await onConfirm({
        status: 'DELIVERED',
        actualDelivery: values.actualDelivery.format('YYYY-MM-DD'),
        deliveryItems: values.deliveryItems || [],
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal open width={620} footer={null} closable={!saving} maskClosable={!saving}
      keyboard={!saving} onCancel={() => { if (!saving) onCancel(); }}
      title={t('transport:orderTransports.changeStatusTitle', { number: transport.routeNumber })}>
      <Form form={form} layout="vertical" onFinish={save} initialValues={{
        actualDelivery: transport.actualDelivery ? dayjs(transport.actualDelivery) : null,
        deliveryItems: buildTransportDeliveryFormItems(transport.cargoItems),
      }}>
        <Alert type="info" showIcon message={t('transport:orderTransports.deliveredStatusHint')} style={{ marginBottom: 16 }} />
        <Form.Item name="actualDelivery" label={t('transport:fields.actualDelivery')}
          rules={[{ required: true, message: t('transport:validation.actualDeliveryRequired') }]}>
          <DatePicker {...transportDatePickerProps} style={{ width: '100%' }} />
        </Form.Item>
        <TransportDeliveryQuantityFields cargoItems={transport.cargoItems} routeOrders={transport.routeOrders} />
        <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button disabled={saving} onClick={onCancel}>{t('common:cancel')}</Button>
          <Button type="primary" htmlType="submit" loading={saving} disabled={missingIdentity}>
            {t('transport:updateStatus')}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
}
